import cx from 'classnames'
import { isNil } from 'lodash'
import { Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useGroupActions } from 'hooks'
import { ROUTE_PATH } from 'lib/paths'
import { addNotification } from 'store/slices/notifications'
import { Link } from 'features/i18n/router'
import { Group, GroupRelationship, Post } from 'lib/_types'
import { selectIsLoggedIn } from 'store/selectors'
import { useCurrentUser } from 'store/api/yay/user'
import {
  useGetGroupMutateMutation,
  useGetGroupsJoinedStatusesQuery,
} from 'store/api/yay/group'

import Button from 'components/Button'
import GroupImage from 'features/Group/GroupImage'

import './style.scss'

const PostShareableGroup = ({
  post,
  show = true,
  disabled,
}: {
  post: Partial<Post>
  show?: boolean
  disabled?: boolean
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { data: currentUser } = useCurrentUser()

  const { attemptJoinGroup, GroupActionConfirmBox } = useGroupActions()

  const shareableGroup = post?.shareable?.group

  const groupsJoinedStatusesRes = useGetGroupsJoinedStatusesQuery(
    {
      ids: [shareableGroup?.id],
    },
    {
      skip:
        !isLoggedIn ||
        isNil(shareableGroup?.id) ||
        post?.post_type != 'shareable_group' ||
        shareableGroup?.owner?.id == currentUser?.id ||
        !isNil(post?._groupRelationship),
    }
  )

  const [getGroupMutate] = useGetGroupMutateMutation()

  // `post?._groupRelationship` is only available in timeline pages.
  // For other pages, like post, conversation, or reposts,
  // we shall rely on `groupsJoinedStatusesRes`.
  const groupsJoinedStatuses =
    post?._groupRelationship ??
    groupsJoinedStatusesRes?.data?.[shareableGroup?.id]

  if (!shareableGroup) return null

  return (
    <>
      <div
        className={cx('PostShareableGroup', {
          'PostShareableGroup--disabled': disabled,
        })}
      >
        <div className="PostShareableGroup__wrapper">
          <Link
            className="PostShareableGroup__link"
            to={ROUTE_PATH.GROUP(shareableGroup.id)}
            onClick={(e) => {
              if (groupsJoinedStatuses == GroupRelationship.Banned) {
                e.preventDefault()

                dispatch(
                  addNotification({
                    message: t('server_error.-103'),
                    type: 'error',
                    variant: 'snackbar',
                  })
                )

                return
              }
            }}
          />

          <GroupImage group={shareableGroup as Group} />

          <div className="PostShareableGroup__content">
            <p className="PostShareableGroup__title">{shareableGroup.topic}</p>

            <p className="PostShareableGroup__subtitle">
              {t('post.post_share_group.user_count', {
                members: (
                  shareableGroup.groups_users_count || 0
                ).toLocaleString(),
              })}
            </p>
          </div>

          {show && (
            <Fragment>
              {groupsJoinedStatuses == GroupRelationship.Pending && (
                <Button
                  className={'PostShareableGroup__join'}
                  buttonStyle="red"
                  onClick={async () => {
                    await attemptJoinGroup(
                      {
                        ...shareableGroup,
                        is_pending: true,
                      } as Group,
                      () => {
                        // There seems to be a lag from BE here.
                        window.setTimeout(() => {
                          groupsJoinedStatusesRes.refetch()
                        }, 400)
                      }
                    )
                  }}
                >
                  {t(
                    'with_group_action.confirm_box.join_private_cancel.cancel'
                  )}
                </Button>
              )}

              {groupsJoinedStatuses == GroupRelationship.Unjoined && (
                <Button
                  className="PostShareableGroup__join"
                  buttonStyle="primary"
                  onClick={async () => {
                    const groupRes = await getGroupMutate({
                      groupId: post?.shareable?.group?.id?.toString?.(),
                    })

                    const attemptJoinGroupCallback = () => {
                      window.location.href = ROUTE_PATH.GROUP(shareableGroup.id)
                    }

                    // `post?.shareable?.group` does not contain `guidelines`.
                    const attemptJoinGroupRes = await attemptJoinGroup(
                      groupRes?.['data']?.group,
                      () => {
                        if (groupRes?.['data']?.group?.is_private) {
                          groupsJoinedStatusesRes.refetch()
                        } else {
                          attemptJoinGroupCallback()
                        }
                      }
                    )

                    if (attemptJoinGroupRes?.['data']?.status == 'success') {
                      attemptJoinGroupCallback()
                    }
                  }}
                >
                  {t('common.join_action')}
                </Button>
              )}
            </Fragment>
          )}
        </div>
      </div>

      <GroupActionConfirmBox />
    </>
  )
}

export default PostShareableGroup
